import { useEffect, useState } from 'react'
import { trackEvent } from '@/utils/analytics'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import BoardLayout from '@/components/layout/BoardLayout'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import { useAppStore } from '@/store'
import { apiService } from '@/service/api'
import type { DocumentRecord } from '@/types/catalog'
import ChatInterface from '@/components/ChatInterface'
import AiChatButton from '@/components/AiChatButton'
import TimeseriesPreview from '@/components/TimeseriesPreview'
import { toast } from 'sonner'
import clsx from 'clsx'
import { X, Database, File, ChevronRight, PanelsTopLeft } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useUIv2 } from '@/hooks/useUIv2'
import AppSidebar from '@/components/AppSidebar'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import { useRenameDocument } from '@/hooks/useRenameDocument'
import DatasheetPreview from '@/components/DatasheetPreview'

// Timeseries documents carry tag/value counts in `properties`. These are not
// part of the shared `DocumentRecord['properties']` shape in @/types/catalog,
// so we narrow to a timeseries-specific view locally. See crossFileNeeds in
// the FS typecheck-gate work: ideally these become optional fields on
// DocumentRecord['properties'].
type TimeseriesProperties = DocumentRecord['properties'] & {
  tag_count?: number
  value_count?: number
}

const Timeseries = () => {
  const isV2 = useUIv2()
  const { doc_id } = useParams()
  const { t } = useTranslation(['data_catalog', 'document_viewer'])
  const [searchParams] = useSearchParams()
  // boardName comes only from the ?boardName= query param (set when navigating
  // from a board). Documents opened from the Data Catalog have no param, so the
  // breadcrumb shows "Data Catalog" rather than the active/Master board.
  const boardNameParam = searchParams.get('boardName')
  const docNameParam = searchParams.get('docName')
  // Deep-link from Tag mapping: open the sheet with the tag's COLUMN
  // emphasised and scrolled into view. Deliberately not a search — search
  // scans cell values and a signal name is a header, so seeding it reports
  // "No matches" for a column in plain view.
  // so a matched tag lands ON its data instead of on a 556-column file.
  const tagParam = searchParams.get('tag')
  const navigateBack = useNavigateBack()
  const navigate = useNavigate()

  const cameFromBoard = typeof window !== 'undefined' && window.document.referrer && (() => {
    try { return new URL(window.document.referrer).pathname.startsWith('/board/') } catch { return false }
  })()
  const boardName = boardNameParam || (cameFromBoard ? t('document_viewer:common.board', { defaultValue: 'Board' }) : null)
  const { setContextId } = useAppStore()
  const [document, setDocument] = useState<DocumentRecord | null>(null)
  const [chatOpen, setChatOpen] = useState(false)
  const tsProperties: TimeseriesProperties | undefined = document?.properties

  useEffect(() => {
    const fetchDocument = async () => {
      if (!doc_id) return
      try {
        const doc = await apiService.getDocument(doc_id)
        setDocument(doc)
        const docProperties: TimeseriesProperties = doc.properties
        trackEvent('timeseries-explored', {
          document_id: doc_id,
          tag_count: docProperties.tag_count || 0,
        })
        setContextId(doc_id)
      } catch {
        toast.error(t('document_viewer:common.fetchError', { defaultValue: 'Error fetching document' }))
      }
    }
    fetchDocument()
  }, [doc_id, setContextId])

  const renameDocument = useRenameDocument()
  const handleRename = (newName: string) => {
    if (!document) return
    void renameDocument(doc_id, newName, {
      previousName: document.name,
      applyName: (n) => setDocument((d) => (d ? { ...d, name: n } : d)),
    })
  }

  const handleChatOpen = () => {
    if (!chatOpen) {
      if (doc_id) trackEvent('timeseries-chat_opened', { document_id: doc_id })
    }
    setChatOpen(!chatOpen)
  }

  const closeAction = {
    onClick: navigateBack,
    element: (
      <button
        type="button"
        className="p-2 text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-accent rounded-lg transition-colors"
        title={t('common:buttons.close')}
      >
        <X className="w-5 h-5" />
      </button>
    ),
  }

  // ── V2 layout: AppSidebar + breadcrumb navbar ──
  if (isV2) {
    return (
      <div className="flex h-screen">
        <AppSidebar />
        <div className="flex flex-1 flex-col min-w-0">
          {/* Breadcrumb navbar */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <button
                type="button"
                onClick={boardName ? navigateBack : () => navigate('/data-catalog')}
                className="flex items-center gap-2 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
              >
                {boardName ? (
                  <PanelsTopLeft className="w-[18px] h-[18px]" />
                ) : (
                  <Database className="w-[18px] h-[18px]" />
                )}
                <span className="text-base">{boardName || t('nav:menu.data', 'Data')}</span>
              </button>
              <ChevronRight className="w-[15px] h-[15px] text-muted-foreground shrink-0" />
              <div className="flex items-center gap-2 min-w-0">
                <File className="w-[18px] h-[18px] text-foreground shrink-0" />
                {document?.name || docNameParam ? (
                  <FileNameWithCopy
                    name={document?.name || docNameParam}
                    className="text-base font-normal"
                    onRename={document ? handleRename : undefined}
                  />
                ) : (
                  <span className="text-base text-foreground truncate">{t('document_viewer:common.loading', { defaultValue: 'Loading...' })}</span>
                )}
              </div>
            </div>
          </div>

          {/* Spreadsheet viewer — uses DatasheetPreview with zoom, sheet tabs, column headers */}
          <div className="flex-1 min-h-0">
            <DatasheetPreview
              docId={doc_id!}
              className="h-full"
              highlightColumn={tagParam ?? undefined}
            />
          </div>
        </div>
      </div>
    )
  }

  // ── Legacy layout ──
  return (
    <BoardLayout
      name={boardName || t('nav:menu.data', 'Data')}
      backUrl={''}
      handleBackCallback={navigateBack}
      actions={[closeAction]}
    >
      <div className="flex h-full w-full">
        <div
          className={clsx(
            'h-full relative transition-all',
            chatOpen ? 'w-8/12' : 'w-full'
          )}
        >
          <div className="h-full flex flex-col gap-4 p-4 min-h-0">
            <div className="shrink-0 flex flex-col gap-1">
              {document?.name ? (
                <FileNameWithCopy name={document.name} onRename={handleRename} />
              ) : (
                <h2 className="text-lg font-semibold">{t('document_viewer:common.loading', { defaultValue: 'Loading...' })}</h2>
              )}
              <div className="text-sm text-gray-500 dark:text-dark-text-muted flex gap-4">
                {tsProperties?.tag_count && (
                  <span>{t('document_viewer:timeseries.tags', { count: tsProperties.tag_count, defaultValue: 'Tags: {{count}}' })}</span>
                )}
                {tsProperties?.value_count && (
                  <span>{t('document_viewer:timeseries.values', { count: tsProperties.value_count, defaultValue: 'Values: {{count}}' })}</span>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0">
              <TimeseriesPreview docId={doc_id!} />
            </div>
          </div>
          <AiChatButton isOpen={chatOpen} onClick={handleChatOpen} />
        </div>
        <div
          className={clsx(
            'h-full overflow-hidden transition-all border-l border-gray-100 dark:border-dark-border',
            chatOpen ? 'w-4/12' : 'w-0'
          )}
        >
          {doc_id && (
            <ChatInterface
              context_id={doc_id}
              context_type="document"
              onClose={handleChatOpen}
              chatMode="canvas"
            />
          )}
        </div>
      </div>
    </BoardLayout>
  )
}

export default Timeseries
